import { useState, useMemo } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { Check, ChevronsUpDown, Clock } from 'lucide-react';

/**
 * Common IANA timezone list grouped by region.
 * Covers the most-used zones; Intl.supportedValuesOf('timeZone') is available
 * in modern browsers but produces 400+ entries — this curated list is better UX.
 */
const TIMEZONE_GROUPS: Array<{ group: string; zones: Array<{ value: string; label: string }> }> = [
  {
    group: 'United States',
    zones: [
      { value: 'America/New_York',    label: 'Eastern Time (New York)'       },
      { value: 'America/Chicago',     label: 'Central Time (Chicago)'        },
      { value: 'America/Denver',      label: 'Mountain Time (Denver)'        },
      { value: 'America/Phoenix',     label: 'Mountain Time – Arizona (no DST)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)'    },
      { value: 'America/Anchorage',   label: 'Alaska Time (Anchorage)'       },
      { value: 'Pacific/Honolulu',    label: 'Hawaii Time (Honolulu)'        },
    ],
  },
  {
    group: 'Canada',
    zones: [
      { value: 'America/Toronto',     label: 'Eastern Time (Toronto)'        },
      { value: 'America/Winnipeg',    label: 'Central Time (Winnipeg)'       },
      { value: 'America/Edmonton',    label: 'Mountain Time (Edmonton)'      },
      { value: 'America/Vancouver',   label: 'Pacific Time (Vancouver)'      },
      { value: 'America/Halifax',     label: 'Atlantic Time (Halifax)'       },
      { value: 'America/St_Johns',    label: "Newfoundland Time (St. John's)" },
    ],
  },
  {
    group: 'Europe',
    zones: [
      { value: 'Europe/London',       label: 'GMT / BST (London)'            },
      { value: 'Europe/Dublin',       label: 'GMT / IST (Dublin)'            },
      { value: 'Europe/Paris',        label: 'Central European Time (Paris)' },
      { value: 'Europe/Berlin',       label: 'Central European Time (Berlin)'},
      { value: 'Europe/Amsterdam',    label: 'Central European Time (Amsterdam)' },
      { value: 'Europe/Rome',         label: 'Central European Time (Rome)'  },
      { value: 'Europe/Madrid',       label: 'Central European Time (Madrid)'},
      { value: 'Europe/Warsaw',       label: 'Central European Time (Warsaw)'},
      { value: 'Europe/Athens',       label: 'Eastern European Time (Athens)'},
      { value: 'Europe/Helsinki',     label: 'Eastern European Time (Helsinki)' },
      { value: 'Europe/Istanbul',     label: 'Turkey Time (Istanbul)'        },
      { value: 'Europe/Moscow',       label: 'Moscow Time (Moscow)'          },
    ],
  },
  {
    group: 'Israel & Middle East',
    zones: [
      { value: 'Asia/Jerusalem',      label: 'Israel Time (Jerusalem)'       },
      { value: 'Asia/Dubai',          label: 'Gulf Standard Time (Dubai)'    },
      { value: 'Asia/Riyadh',         label: 'Arabia Standard Time (Riyadh)' },
      { value: 'Asia/Tehran',         label: 'Iran Time (Tehran)'            },
      { value: 'Asia/Kuwait',         label: 'Arabia Standard Time (Kuwait)' },
      { value: 'Asia/Beirut',         label: 'Eastern European Time (Beirut)'},
      { value: 'Asia/Amman',          label: 'Eastern European Time (Amman)' },
    ],
  },
  {
    group: 'Asia & Pacific',
    zones: [
      { value: 'Asia/Kolkata',        label: 'India Standard Time (Kolkata)' },
      { value: 'Asia/Karachi',        label: 'Pakistan Time (Karachi)'       },
      { value: 'Asia/Dhaka',          label: 'Bangladesh Time (Dhaka)'       },
      { value: 'Asia/Bangkok',        label: 'Indochina Time (Bangkok)'      },
      { value: 'Asia/Singapore',      label: 'Singapore Time (Singapore)'    },
      { value: 'Asia/Shanghai',       label: 'China Standard Time (Shanghai)'},
      { value: 'Asia/Tokyo',          label: 'Japan Standard Time (Tokyo)'   },
      { value: 'Asia/Seoul',          label: 'Korea Standard Time (Seoul)'   },
      { value: 'Australia/Sydney',    label: 'Eastern Australia (Sydney)'    },
      { value: 'Australia/Melbourne', label: 'Eastern Australia (Melbourne)' },
      { value: 'Australia/Perth',     label: 'Western Australia (Perth)'     },
      { value: 'Pacific/Auckland',    label: 'New Zealand Time (Auckland)'   },
    ],
  },
  {
    group: 'Latin America',
    zones: [
      { value: 'America/Mexico_City', label: 'Central Time (Mexico City)'    },
      { value: 'America/Bogota',      label: 'Colombia Time (Bogotá)'        },
      { value: 'America/Lima',        label: 'Peru Time (Lima)'              },
      { value: 'America/Sao_Paulo',   label: 'Brasilia Time (São Paulo)'     },
      { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (Buenos Aires)' },
      { value: 'America/Santiago',    label: 'Chile Time (Santiago)'         },
    ],
  },
  {
    group: 'Africa',
    zones: [
      { value: 'Africa/Cairo',        label: 'Egypt Standard Time (Cairo)'   },
      { value: 'Africa/Johannesburg', label: 'South Africa Time (Johannesburg)' },
      { value: 'Africa/Nairobi',      label: 'East Africa Time (Nairobi)'    },
      { value: 'Africa/Lagos',        label: 'West Africa Time (Lagos)'      },
    ],
  },
  {
    group: 'UTC / Other',
    zones: [
      { value: 'UTC',                 label: 'UTC (Coordinated Universal Time)' },
      { value: 'Etc/GMT+12',          label: 'UTC−12:00'                     },
      { value: 'Etc/GMT+5',           label: 'UTC−05:00'                     },
    ],
  },
];

/** Flat list for search. */
const ALL_ZONES = TIMEZONE_GROUPS.flatMap((g) => g.zones);

function getLabel(value: string): string {
  return ALL_ZONES.find((z) => z.value === value)?.label ?? value;
}

interface TimezonePickerProps {
  value: string;
  onChange: (tz: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function TimezonePicker({
  value,
  onChange,
  placeholder = 'Select timezone…',
  disabled = false,
  className,
}: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return TIMEZONE_GROUPS;
    const q = search.toLowerCase();
    return TIMEZONE_GROUPS
      .map((g) => ({
        ...g,
        zones: g.zones.filter(
          (z) => z.label.toLowerCase().includes(q) || z.value.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.zones.length > 0);
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="flex items-center gap-2 truncate">
            <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {value ? getLabel(value) : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search timezone…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-64">
            {filteredGroups.length === 0 && (
              <CommandEmpty>No timezone found.</CommandEmpty>
            )}
            {filteredGroups.map((g) => (
              <CommandGroup key={g.group} heading={g.group}>
                {g.zones.map((z) => (
                  <CommandItem
                    key={z.value}
                    value={z.value}
                    onSelect={() => {
                      onChange(z.value);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        value === z.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {z.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
